// src/data-table/index.tsx
import {
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useState as useState2, useCallback as useCallback2, useRef as useRef2, useMemo as useMemo2 } from "react";
import { cn as cn4 } from "@modootoday/ui-primitives";

// src/data-table/context.ts
import { createContext, useContext } from "react";
var DataTableContext = createContext(null);
var DataTableProvider = DataTableContext.Provider;
function useDataTable() {
  const ctx = useContext(DataTableContext);
  if (!ctx) throw new Error("useDataTable must be used within DataTable");
  return ctx;
}

// src/data-table/toolbar.tsx
import {
  Button,
  Checkbox,
  cn,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@modootoday/ui-primitives";
import {
  Check,
  Columns3,
  Download,
  RefreshCw,
  Search,
  X
} from "lucide-react";

// src/data-table/csv-export.ts
function exportToCSV(table, filename) {
  const headers = table.getVisibleLeafColumns().map(
    (c) => typeof c.columnDef.header === "string" ? c.columnDef.header : c.id
  );
  const rows = table.getFilteredRowModel().rows.map(
    (row) => row.getVisibleCells().map((cell) => {
      const val = cell.getValue();
      const str = val == null ? "" : String(val);
      return str.includes(",") || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
    })
  );
  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// src/data-table/toolbar.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var ICON = "size-3.5 shrink-0";
function DataTableToolbar() {
  const {
    table,
    isLoading,
    searchInput,
    handleSearchInput,
    clearSearch,
    activeFilterCount,
    clearAllFilters,
    exportFilename,
    searchPlaceholder,
    title,
    onRefresh,
    isRefreshing,
    toolbarExtras,
    hideExport,
    hideColumnVisibility,
    applyFilters,
    hasPendingChanges,
    isBusy
  } = useDataTable();
  const hasAnyFilter = activeFilterCount > 0 || !!searchInput;
  const isDim = isLoading || isBusy;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex shrink-0 items-center gap-2 px-3 py-3 transition-opacity",
        isDim && "pointer-events-none opacity-60"
      ),
      children: [
        title && /* @__PURE__ */ jsx("div", { className: "mr-1 shrink-0 text-[13px] font-semibold text-text-primary", children: title }),
        /* @__PURE__ */ jsx(
          Input,
          {
            value: searchInput,
            onChange: (e) => handleSearchInput(e.target.value),
            placeholder: searchPlaceholder,
            leadingIcon: /* @__PURE__ */ jsx(Search, { className: ICON }),
            trailingIcon: searchInput ? /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: clearSearch,
                "aria-label": "\uAC80\uC0C9\uC5B4 \uC9C0\uC6B0\uAE30",
                className: "flex size-5 items-center justify-center rounded-xs text-text-tertiary hover:bg-surface-sunken hover:text-text-primary",
                children: /* @__PURE__ */ jsx(X, { className: "size-3" })
              }
            ) : null,
            className: "h-8 min-w-0 flex-1 shrink basis-40 text-[12px] sm:max-w-56"
          }
        ),
        hasPendingChanges && /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "default",
            size: "sm",
            onClick: applyFilters,
            className: "gap-1.5",
            children: [
              /* @__PURE__ */ jsx(Check, { className: ICON, "aria-hidden": true }),
              "\uD544\uD130 \uC801\uC6A9"
            ]
          }
        ),
        hasAnyFilter && /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: clearAllFilters,
            className: "gap-1.5",
            children: [
              /* @__PURE__ */ jsx(X, { className: ICON, "aria-hidden": true }),
              "\uD544\uD130 \uCD08\uAE30\uD654",
              activeFilterCount > 0 && /* @__PURE__ */ jsx("span", { className: "ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-xs bg-accent px-1 text-[10px] font-semibold text-text-inverse tabular-nums", children: activeFilterCount })
            ]
          }
        ),
        toolbarExtras,
        /* @__PURE__ */ jsx("div", { className: "ml-auto" }),
        onRefresh && /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: onRefresh,
            disabled: isRefreshing,
            className: "gap-1.5",
            "aria-label": "\uC0C8\uB85C\uACE0\uCE68",
            children: [
              /* @__PURE__ */ jsx(
                RefreshCw,
                {
                  className: cn(ICON, isRefreshing && "animate-spin"),
                  "aria-hidden": true
                }
              ),
              "\uC0C8\uB85C\uACE0\uCE68"
            ]
          }
        ),
        !hideColumnVisibility && /* @__PURE__ */ jsxs(Popover, { children: [
          /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: "gap-1.5", children: [
            /* @__PURE__ */ jsx(Columns3, { className: ICON, "aria-hidden": true }),
            "\uC5F4 \uC120\uD0DD"
          ] }) }),
          /* @__PURE__ */ jsxs(PopoverContent, { className: "w-48 p-2", align: "end", children: [
            /* @__PURE__ */ jsx("p", { className: "mb-2 px-1 text-[11px] font-medium text-text-tertiary", children: "\uD45C\uC2DC\uD560 \uC5F4 \uC120\uD0DD" }),
            /* @__PURE__ */ jsx("div", { className: "max-h-60 space-y-0.5 overflow-y-auto", children: table.getAllLeafColumns().map((col) => /* @__PURE__ */ jsxs(
              "label",
              {
                className: "flex cursor-pointer items-center gap-2 rounded-xs px-1.5 py-1 text-[12px] hover:bg-surface-sunken",
                children: [
                  /* @__PURE__ */ jsx(
                    Checkbox,
                    {
                      checked: col.getIsVisible(),
                      onCheckedChange: (v) => col.toggleVisibility(!!v)
                    }
                  ),
                  /* @__PURE__ */ jsx("span", { className: "truncate", children: typeof col.columnDef.header === "string" ? col.columnDef.header : col.id })
                ]
              },
              col.id
            )) })
          ] })
        ] }),
        !hideExport && /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: () => exportToCSV(table, exportFilename),
            className: "gap-1.5",
            children: [
              /* @__PURE__ */ jsx(Download, { className: ICON, "aria-hidden": true }),
              "CSV"
            ]
          }
        )
      ]
    }
  );
}

// src/data-table/head.tsx
import { flexRender } from "@tanstack/react-table";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { Skeleton } from "@modootoday/ui-primitives";

// src/data-table/column-filter.tsx
import { useState, useEffect, useMemo } from "react";
import {
  cn as cn2,
  Popover as Popover2,
  PopoverTrigger as PopoverTrigger2,
  PopoverContent as PopoverContent2
} from "@modootoday/ui-primitives";
import { Filter } from "lucide-react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function ColumnFilterPopover({
  column
}) {
  const [open, setOpen] = useState(false);
  const { pendingFilters, setPendingColumnFilter } = useDataTable();
  const meta = column.columnDef.meta ?? {};
  const filterType = meta.filterType ?? "text";
  const pendingValue = useMemo(
    () => pendingFilters.find((f) => f.id === column.id)?.value,
    [pendingFilters, column.id]
  );
  const hasPending = filterTypeHasValue(filterType, pendingValue);
  const columnLabel = typeof column.columnDef.header === "string" ? column.columnDef.header : column.id;
  return /* @__PURE__ */ jsxs2(Popover2, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx2(PopoverTrigger2, { asChild: true, children: /* @__PURE__ */ jsx2(
      "button",
      {
        onClick: (e) => e.stopPropagation(),
        className: cn2(
          "rounded p-0.5 transition-colors hover:bg-foreground/10",
          hasPending ? "text-text-brand" : "text-text-tertiary/50"
        ),
        "aria-label": `${columnLabel} \uD544\uD130`,
        children: /* @__PURE__ */ jsx2(Filter, { className: "size-3" })
      }
    ) }),
    /* @__PURE__ */ jsxs2(PopoverContent2, { className: "w-60 p-2", align: "start", children: [
      /* @__PURE__ */ jsx2("div", { className: "mb-1 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary", children: columnLabel }),
      filterType === "select" ? /* @__PURE__ */ jsx2(
        SelectFilter,
        {
          options: meta.filterOptions ?? [],
          value: pendingValue ?? "",
          placeholder: meta.filterPlaceholder ?? "\uC120\uD0DD...",
          onChange: (v) => setPendingColumnFilter(column.id, v === "" ? void 0 : v)
        }
      ) : filterType === "range" ? /* @__PURE__ */ jsx2(
        RangeFilter,
        {
          value: pendingValue ?? {},
          min: meta.filterMin,
          max: meta.filterMax,
          step: meta.filterStep,
          onChange: (v) => setPendingColumnFilter(
            column.id,
            v.min == null && v.max == null ? void 0 : v
          )
        }
      ) : filterType === "date" ? /* @__PURE__ */ jsx2(
        DateFilter,
        {
          value: pendingValue ?? "",
          onChange: (v) => setPendingColumnFilter(column.id, v === "" ? void 0 : v)
        }
      ) : filterType === "date-range" ? /* @__PURE__ */ jsx2(
        DateRangeFilter,
        {
          value: pendingValue ?? {},
          onChange: (v) => setPendingColumnFilter(
            column.id,
            !v.from && !v.to ? void 0 : v
          )
        }
      ) : /* @__PURE__ */ jsx2(
        TextFilter,
        {
          value: pendingValue ?? "",
          placeholder: meta.filterPlaceholder ?? `${columnLabel} \uD544\uD130...`,
          onChange: (v) => setPendingColumnFilter(column.id, v === "" ? void 0 : v)
        }
      ),
      hasPending && /* @__PURE__ */ jsx2(
        "button",
        {
          onClick: () => {
            setPendingColumnFilter(column.id, void 0);
            setOpen(false);
          },
          className: "mt-1.5 w-full rounded-sm bg-surface-secondary px-2 py-1 text-center text-[11px] text-text-tertiary hover:bg-surface-secondary/80",
          children: "\uAC12 \uC9C0\uC6B0\uAE30"
        }
      ),
      /* @__PURE__ */ jsx2("p", { className: "mt-2 px-0.5 text-[10px] text-text-tertiary", children: '\uBCC0\uACBD\uC740 \uD234\uBC14\uC758 "\uD544\uD130 \uC801\uC6A9" \uBC84\uD2BC\uC744 \uB20C\uB7EC\uC57C \uBC18\uC601\uB429\uB2C8\uB2E4.' })
    ] })
  ] });
}
function TextFilter({
  value,
  placeholder,
  onChange
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return /* @__PURE__ */ jsx2(
    "input",
    {
      value: local,
      onChange: (e) => {
        setLocal(e.target.value);
        onChange(e.target.value);
      },
      placeholder,
      className: "w-full rounded-sm border border-stroke-subtle bg-surface-base px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-accent/20",
      autoFocus: true
    }
  );
}
function SelectFilter({
  options,
  value,
  placeholder,
  onChange
}) {
  return /* @__PURE__ */ jsxs2(
    "select",
    {
      value,
      onChange: (e) => onChange(e.target.value),
      className: "w-full rounded-sm border border-stroke-subtle bg-surface-base px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-accent/20",
      children: [
        /* @__PURE__ */ jsx2("option", { value: "", children: placeholder }),
        options.map((opt) => /* @__PURE__ */ jsx2("option", { value: opt.value, children: opt.label }, opt.value))
      ]
    }
  );
}
function RangeFilter({
  value,
  min,
  max,
  step,
  onChange
}) {
  const [localMin, setLocalMin] = useState(
    value.min != null ? String(value.min) : ""
  );
  const [localMax, setLocalMax] = useState(
    value.max != null ? String(value.max) : ""
  );
  useEffect(() => {
    setLocalMin(value.min != null ? String(value.min) : "");
    setLocalMax(value.max != null ? String(value.max) : "");
  }, [value.min, value.max]);
  const commit = (nextMin, nextMax) => {
    const parsedMin = nextMin === "" ? void 0 : Number(nextMin);
    const parsedMax = nextMax === "" ? void 0 : Number(nextMax);
    onChange({
      min: Number.isFinite(parsedMin) ? parsedMin : void 0,
      max: Number.isFinite(parsedMax) ? parsedMax : void 0
    });
  };
  return /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1.5", children: [
    /* @__PURE__ */ jsx2(
      "input",
      {
        type: "number",
        value: localMin,
        min,
        max,
        step,
        onChange: (e) => {
          setLocalMin(e.target.value);
          commit(e.target.value, localMax);
        },
        placeholder: "\uCD5C\uC18C",
        className: "w-full rounded-sm border border-stroke-subtle bg-surface-base px-2 py-1.5 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-accent/20"
      }
    ),
    /* @__PURE__ */ jsx2("span", { className: "text-[11px] text-text-tertiary", children: "~" }),
    /* @__PURE__ */ jsx2(
      "input",
      {
        type: "number",
        value: localMax,
        min,
        max,
        step,
        onChange: (e) => {
          setLocalMax(e.target.value);
          commit(localMin, e.target.value);
        },
        placeholder: "\uCD5C\uB300",
        className: "w-full rounded-sm border border-stroke-subtle bg-surface-base px-2 py-1.5 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-accent/20"
      }
    )
  ] });
}
function DateFilter({
  value,
  onChange
}) {
  return /* @__PURE__ */ jsx2(
    "input",
    {
      type: "date",
      value,
      onChange: (e) => onChange(e.target.value),
      className: "w-full rounded-sm border border-stroke-subtle bg-surface-base px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-accent/20"
    }
  );
}
function DateRangeFilter({
  value,
  onChange
}) {
  return /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1.5", children: [
    /* @__PURE__ */ jsx2(
      "input",
      {
        type: "date",
        value: value.from ?? "",
        onChange: (e) => onChange({ ...value, from: e.target.value || void 0 }),
        className: "w-full rounded-sm border border-stroke-subtle bg-surface-base px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-accent/20"
      }
    ),
    /* @__PURE__ */ jsx2("span", { className: "text-[11px] text-text-tertiary", children: "~" }),
    /* @__PURE__ */ jsx2(
      "input",
      {
        type: "date",
        value: value.to ?? "",
        onChange: (e) => onChange({ ...value, to: e.target.value || void 0 }),
        className: "w-full rounded-sm border border-stroke-subtle bg-surface-base px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-accent/20"
      }
    )
  ] });
}
function filterTypeHasValue(type, value) {
  if (value == null) return false;
  if (type === "text" || type === "select" || type === "date") {
    return typeof value === "string" && value !== "";
  }
  if (type === "range") {
    const v = value;
    return v.min != null || v.max != null;
  }
  if (type === "date-range") {
    const v = value;
    return Boolean(v.from) || Boolean(v.to);
  }
  return false;
}

// src/data-table/head.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function DataTableHead() {
  const { table, isLoading, columns } = useDataTable();
  if (isLoading) {
    return /* @__PURE__ */ jsx3("thead", { className: "sticky top-0 z-10 [&_th]:bg-surface-sunken", children: /* @__PURE__ */ jsxs3("tr", { className: "border-b border-stroke-subtle", children: [
      /* @__PURE__ */ jsx3("th", { className: "w-10 border-r border-stroke-subtle px-2 py-2.5", children: /* @__PURE__ */ jsx3(Skeleton, { className: "mx-auto h-3 w-4" }) }),
      Array.from({ length: Math.min(columns.length, 8) }, (_, i) => /* @__PURE__ */ jsx3(
        "th",
        {
          className: "border-r border-stroke-subtle px-3 py-2.5 last:border-r-0",
          children: /* @__PURE__ */ jsx3(
            Skeleton,
            {
              className: "h-3 w-16",
              style: { animationDelay: `${i * 50}ms` }
            }
          )
        },
        i
      ))
    ] }) });
  }
  return /* @__PURE__ */ jsx3("thead", { className: "sticky top-0 z-10 [&_th]:bg-surface-sunken", children: table.getHeaderGroups().map((hg) => /* @__PURE__ */ jsxs3("tr", { className: "border-b border-stroke-subtle", children: [
    /* @__PURE__ */ jsx3("th", { className: "w-10 border-r border-stroke-subtle px-2 py-2.5 text-center text-[10px] font-semibold text-text-tertiary", children: "#" }),
    hg.headers.map((header) => {
      if (header.isPlaceholder) {
        return /* @__PURE__ */ jsx3(
          "th",
          {
            className: "border-r border-stroke-subtle px-3 py-2.5 last:border-r-0"
          },
          header.id
        );
      }
      const canSort = header.column.getCanSort();
      const canFilter = header.column.getCanFilter();
      const sortDir = header.column.getIsSorted();
      return /* @__PURE__ */ jsx3(
        "th",
        {
          className: "border-r border-stroke-subtle px-3 py-2.5 text-left text-[11px] font-semibold text-text-tertiary last:border-r-0",
          children: /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-1", children: [
            /* @__PURE__ */ jsxs3(
              "button",
              {
                className: "inline-flex min-w-0 flex-1 items-center gap-1 hover:text-text-primary disabled:cursor-default disabled:hover:text-text-tertiary",
                onClick: header.column.getToggleSortingHandler(),
                disabled: !canSort,
                children: [
                  /* @__PURE__ */ jsx3("span", { className: "truncate", children: flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  ) }),
                  canSort && (sortDir === "asc" ? /* @__PURE__ */ jsx3(ArrowUp, { className: "size-3 shrink-0 text-text-brand" }) : sortDir === "desc" ? /* @__PURE__ */ jsx3(ArrowDown, { className: "size-3 shrink-0 text-text-brand" }) : /* @__PURE__ */ jsx3(ArrowUpDown, { className: "size-3 shrink-0 opacity-30" }))
                ]
              }
            ),
            canFilter && /* @__PURE__ */ jsx3(ColumnFilterPopover, { column: header.column })
          ] })
        },
        header.id
      );
    })
  ] }, hg.id)) });
}

// src/data-table/body.tsx
import { flexRender as flexRender2 } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn as cn3, Skeleton as Skeleton2 } from "@modootoday/ui-primitives";
import { Search as Search2 } from "lucide-react";

// src/data-table/types.ts
var ROW_HEIGHT = 36;
var SKELETON_ROWS = 12;

// src/data-table/body.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function DataTableBody({
  estimateRowHeight = ROW_HEIGHT,
  scrollContainerRef
}) {
  const {
    rows,
    isLoading,
    columns,
    activeFilterCount,
    searchInput,
    clearAllFilters
  } = useDataTable();
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimateRowHeight,
    overscan: 20
  });
  const virtualRows = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();
  const colCount = columns.length + 1;
  if (isLoading) {
    return /* @__PURE__ */ jsx4("tbody", { children: Array.from({ length: SKELETON_ROWS }, (_, rowIdx) => /* @__PURE__ */ jsxs4(
      "tr",
      {
        className: cn3(
          "border-b border-stroke-subtle",
          rowIdx % 2 === 1 && "bg-surface-secondary/20"
        ),
        children: [
          /* @__PURE__ */ jsx4(
            "td",
            {
              className: "border-r border-stroke-subtle px-2 py-2",
              style: { background: "hsl(var(--secondary) / 0.3)" },
              children: /* @__PURE__ */ jsx4(
                Skeleton2,
                {
                  className: "mx-auto h-3 w-4",
                  style: { animationDelay: `${rowIdx * 40}ms` }
                }
              )
            }
          ),
          Array.from(
            { length: Math.min(columns.length, 8) },
            (_2, colIdx) => /* @__PURE__ */ jsx4(
              "td",
              {
                className: "border-r border-stroke-subtle/50 px-3 py-2 last:border-r-0",
                children: /* @__PURE__ */ jsx4(
                  Skeleton2,
                  {
                    className: cn3(
                      "h-3 rounded",
                      colIdx === 0 ? "w-24" : colIdx % 3 === 0 ? "w-12" : "w-16"
                    ),
                    style: {
                      animationDelay: `${rowIdx * 40 + colIdx * 20}ms`
                    }
                  }
                )
              },
              colIdx
            )
          )
        ]
      },
      rowIdx
    )) });
  }
  if (rows.length === 0) {
    return /* @__PURE__ */ jsx4("tbody", { children: /* @__PURE__ */ jsx4("tr", { children: /* @__PURE__ */ jsx4(
      "td",
      {
        colSpan: colCount,
        className: "px-3 py-12 text-center text-text-tertiary",
        children: /* @__PURE__ */ jsxs4("div", { className: "flex flex-col items-center gap-2", children: [
          /* @__PURE__ */ jsx4(Search2, { className: "size-8 opacity-20" }),
          /* @__PURE__ */ jsx4("p", { className: "text-[13px] font-medium", children: "\uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4" }),
          (activeFilterCount > 0 || searchInput) && /* @__PURE__ */ jsx4(
            "button",
            {
              onClick: clearAllFilters,
              className: "text-[12px] text-text-brand hover:underline",
              children: "\uD544\uD130 \uCD08\uAE30\uD654"
            }
          )
        ] })
      }
    ) }) });
  }
  return /* @__PURE__ */ jsxs4("tbody", { children: [
    virtualRows[0]?.start > 0 && /* @__PURE__ */ jsx4("tr", { children: /* @__PURE__ */ jsx4("td", { colSpan: colCount, style: { height: virtualRows[0].start } }) }),
    virtualRows.map((virtualRow) => {
      const row = rows[virtualRow.index];
      const rowIdx = virtualRow.index;
      return /* @__PURE__ */ jsxs4(
        "tr",
        {
          "data-index": virtualRow.index,
          ref: virtualizer.measureElement,
          className: cn3(
            "border-b border-stroke-subtle transition-colors last:border-0 hover:bg-accent/[0.03]",
            rowIdx % 2 === 1 && "bg-surface-secondary/20"
          ),
          children: [
            /* @__PURE__ */ jsx4(
              "td",
              {
                className: "border-r border-stroke-subtle px-2 py-2 text-center text-[10px] text-text-tertiary",
                style: { background: "hsl(var(--secondary) / 0.3)" },
                children: rowIdx + 1
              }
            ),
            row.getVisibleCells().map((cell) => /* @__PURE__ */ jsx4(
              "td",
              {
                className: "whitespace-nowrap border-r border-stroke-subtle/50 px-3 py-2 last:border-r-0",
                children: flexRender2(cell.column.columnDef.cell, cell.getContext())
              },
              cell.id
            ))
          ]
        },
        row.id
      );
    }),
    virtualRows[virtualRows.length - 1] && /* @__PURE__ */ jsx4("tr", { children: /* @__PURE__ */ jsx4(
      "td",
      {
        colSpan: colCount,
        style: {
          height: totalHeight - (virtualRows[virtualRows.length - 1].start + virtualRows[virtualRows.length - 1].size)
        }
      }
    ) })
  ] });
}

// src/data-table/sentinel.tsx
import { useRef, useEffect as useEffect2, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var VISIBILITY_COOLDOWN_MS = 100;
function DataTableSentinel({
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage
}) {
  const sentinelRef = useRef(null);
  const onFetchRef = useRef(onFetchNextPage);
  onFetchRef.current = onFetchNextPage;
  const cooldownRef = useRef(false);
  const stableFetch = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    if (cooldownRef.current) return;
    onFetchRef.current?.();
  }, []);
  useEffect2(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        cooldownRef.current = true;
        setTimeout(() => {
          cooldownRef.current = false;
        }, VISIBILITY_COOLDOWN_MS);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
  useEffect2(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || !onFetchRef.current || isFetchingNextPage)
      return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          stableFetch();
        }
      },
      // root=null → 브라우저 viewport 기준. 스크롤 구조에 무관.
      { root: null, threshold: 0, rootMargin: "0px 0px 200px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, stableFetch]);
  if (!hasNextPage) return null;
  return /* @__PURE__ */ jsx5("div", { ref: sentinelRef, className: "flex items-center justify-center py-3", children: isFetchingNextPage && /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-1.5 text-[12px] text-text-tertiary", children: [
    /* @__PURE__ */ jsx5(Loader2, { className: "size-3.5 animate-spin" }),
    "\uBD88\uB7EC\uC624\uB294 \uC911..."
  ] }) });
}

// src/data-table/footer.tsx
import { Skeleton as Skeleton3 } from "@modootoday/ui-primitives";
import { Fragment, jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
function DataTableFooter() {
  const { isLoading, filteredRows, totalRows, totalCount, hasNextPage } = useDataTable();
  if (isLoading) {
    return /* @__PURE__ */ jsx6("div", { className: "flex shrink-0 items-center gap-3 px-3 py-3", children: /* @__PURE__ */ jsx6(Skeleton3, { className: "h-3 w-32 rounded" }) });
  }
  return /* @__PURE__ */ jsx6("div", { className: "flex shrink-0 items-center gap-3 px-3 py-3 text-[11px] text-text-tertiary", children: /* @__PURE__ */ jsx6("span", { children: totalCount != null ? /* @__PURE__ */ jsxs6(Fragment, { children: [
    /* @__PURE__ */ jsx6("strong", { className: "font-semibold text-text-primary", children: filteredRows.toLocaleString("ko-KR") }),
    "\uAC74 \uB85C\uB4DC",
    filteredRows !== totalRows && /* @__PURE__ */ jsxs6("span", { className: "ml-1", children: [
      "(",
      totalRows.toLocaleString("ko-KR"),
      "\uAC74 \uC911 \uD544\uD130\uB428)"
    ] }),
    /* @__PURE__ */ jsx6("span", { className: "mx-1.5 text-border", children: "|" }),
    "\uC804\uCCB4",
    " ",
    /* @__PURE__ */ jsx6("strong", { className: "font-semibold text-text-primary", children: totalCount.toLocaleString("ko-KR") }),
    "\uAC74"
  ] }) : /* @__PURE__ */ jsxs6(Fragment, { children: [
    "\uCD1D",
    " ",
    /* @__PURE__ */ jsx6("strong", { className: "font-semibold text-text-primary", children: filteredRows.toLocaleString("ko-KR") }),
    "\uAC74",
    filteredRows !== totalRows && /* @__PURE__ */ jsxs6("span", { className: "ml-1", children: [
      "(",
      totalRows.toLocaleString("ko-KR"),
      "\uAC74 \uC911)"
    ] }),
    hasNextPage && /* @__PURE__ */ jsx6("span", { className: "ml-1 text-text-brand", children: "+" })
  ] }) }) });
}

// src/data-table/index.tsx
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
function DataTable({
  columns,
  data,
  className,
  estimateRowHeight = ROW_HEIGHT,
  exportFilename = "export",
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  isLoading = false,
  isPlaceholderData = false,
  totalCount,
  manualSorting: manualSortingProp,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  onSearchChange,
  searchPlaceholder = "\uC804\uCCB4 \uAC80\uC0C9...",
  title,
  onRefresh,
  isRefreshing = false,
  toolbarExtras,
  hideExport = false,
  hideColumnVisibility = false,
  onColumnFiltersChange
}) {
  const [internalSorting, setInternalSorting] = useState2([]);
  const [columnVisibility, setColumnVisibility] = useState2({});
  const {
    searchInput,
    handleSearchInput,
    clearSearch,
    globalFilter,
    setGlobalFilter
  } = useSearchState(onSearchChange);
  const {
    appliedFilters,
    pendingFilters,
    setPendingColumnFilter,
    applyFilters,
    clearAppliedAndPending,
    hasPendingChanges
  } = useFilterState(onColumnFiltersChange);
  const sorting = sortingProp ?? internalSorting;
  const onSortingChange = onSortingChangeProp ?? setInternalSorting;
  const externalFilterMode = !!onColumnFiltersChange;
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters: externalFilterMode ? [] : appliedFilters,
      columnVisibility
    },
    onSortingChange,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: () => {
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: manualSortingProp,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSortingProp ? void 0 : getSortedRowModel(),
    getFilteredRowModel: externalFilterMode ? void 0 : getFilteredRowModel()
  });
  const { rows } = table.getRowModel();
  const totalRows = data.length;
  const filteredRows = externalFilterMode ? data.length : table.getFilteredRowModel().rows.length;
  const clearAllFilters = useCallback2(() => {
    clearAppliedAndPending();
    clearSearch();
    onRefresh?.();
  }, [clearAppliedAndPending, clearSearch, onRefresh]);
  const scrollContainerRef = useRef2(null);
  const hasAnyRows = rows.length > 0;
  const showSkeleton = isLoading || isRefreshing && !hasAnyRows && !isPlaceholderData;
  const isBusy = !showSkeleton && (isRefreshing || isPlaceholderData && hasAnyRows);
  const ctx = useMemo2(
    () => ({
      table,
      rows,
      isLoading: showSkeleton,
      columns,
      searchInput,
      handleSearchInput,
      clearSearch,
      activeFilterCount: appliedFilters.length,
      clearAllFilters,
      totalRows,
      filteredRows,
      totalCount,
      hasNextPage,
      isFetchingNextPage,
      exportFilename,
      searchPlaceholder,
      title,
      onRefresh,
      isRefreshing,
      toolbarExtras,
      hideExport,
      hideColumnVisibility,
      pendingFilters,
      setPendingColumnFilter,
      applyFilters,
      hasPendingChanges,
      isBusy
    }),
    [
      table,
      rows,
      showSkeleton,
      columns,
      searchInput,
      handleSearchInput,
      clearSearch,
      appliedFilters.length,
      clearAllFilters,
      totalRows,
      filteredRows,
      totalCount,
      hasNextPage,
      isFetchingNextPage,
      exportFilename,
      searchPlaceholder,
      title,
      onRefresh,
      isRefreshing,
      toolbarExtras,
      hideExport,
      hideColumnVisibility,
      pendingFilters,
      setPendingColumnFilter,
      applyFilters,
      hasPendingChanges,
      isBusy
    ]
  );
  return /* @__PURE__ */ jsx7(DataTableProvider, { value: ctx, children: /* @__PURE__ */ jsxs7(
    "div",
    {
      className: cn4("flex h-full flex-col", className),
      "aria-busy": showSkeleton || isBusy || void 0,
      children: [
        /* @__PURE__ */ jsx7(DataTableToolbar, {}),
        /* @__PURE__ */ jsxs7("div", { className: "relative min-h-0 flex-1", children: [
          /* @__PURE__ */ jsxs7(
            "div",
            {
              ref: scrollContainerRef,
              className: cn4(
                "absolute inset-0 overflow-auto rounded-sm border border-stroke-subtle bg-surface-raised",
                isBusy && "pointer-events-none"
              ),
              children: [
                /* @__PURE__ */ jsxs7(
                  "table",
                  {
                    className: cn4(
                      "w-full text-[12px] transition-opacity",
                      isBusy && "opacity-60"
                    ),
                    children: [
                      /* @__PURE__ */ jsx7(DataTableHead, {}),
                      /* @__PURE__ */ jsx7(
                        DataTableBody,
                        {
                          estimateRowHeight,
                          scrollContainerRef
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsx7(
                  DataTableSentinel,
                  {
                    hasNextPage: hasNextPage && !isPlaceholderData,
                    isFetchingNextPage,
                    onFetchNextPage
                  }
                )
              ]
            }
          ),
          isBusy && /* @__PURE__ */ jsx7(BusyOverlay, {})
        ] }),
        /* @__PURE__ */ jsx7(DataTableFooter, {})
      ]
    }
  ) });
}
function useSearchState(onSearchChange) {
  const [searchInput, setSearchInput] = useState2("");
  const [globalFilter, setGlobalFilter] = useState2("");
  const debounceRef = useRef2(null);
  const handleSearchInput = useCallback2(
    (value) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (onSearchChange) onSearchChange(value);
        else setGlobalFilter(value);
      }, 300);
    },
    [onSearchChange]
  );
  const clearSearch = useCallback2(() => {
    setSearchInput("");
    setGlobalFilter("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onSearchChange?.("");
  }, [onSearchChange]);
  return {
    searchInput,
    handleSearchInput,
    clearSearch,
    globalFilter,
    setGlobalFilter
  };
}
function useFilterState(onColumnFiltersChange) {
  const [appliedFilters, setAppliedFilters] = useState2([]);
  const [pendingFilters, setPendingFilters] = useState2([]);
  const setPendingColumnFilter = useCallback2((id, value) => {
    setPendingFilters((prev) => {
      const others = prev.filter((f) => f.id !== id);
      const isEmpty = value == null || value === "" || Array.isArray(value) && value.length === 0;
      return isEmpty ? others : [...others, { id, value }];
    });
  }, []);
  const applyFilters = useCallback2(() => {
    setAppliedFilters(pendingFilters);
    onColumnFiltersChange?.(pendingFilters);
  }, [pendingFilters, onColumnFiltersChange]);
  const clearAppliedAndPending = useCallback2(() => {
    setPendingFilters([]);
    setAppliedFilters([]);
    onColumnFiltersChange?.([]);
  }, [onColumnFiltersChange]);
  const hasPendingChanges = useMemo2(
    () => !sameFilters(pendingFilters, appliedFilters),
    [pendingFilters, appliedFilters]
  );
  return {
    appliedFilters,
    pendingFilters,
    setPendingColumnFilter,
    applyFilters,
    clearAppliedAndPending,
    hasPendingChanges
  };
}
function sameFilters(a, b) {
  if (a.length !== b.length) return false;
  const map = new Map(b.map((f) => [f.id, f.value]));
  for (const f of a) {
    if (!map.has(f.id)) return false;
    if (map.get(f.id) !== f.value) return false;
  }
  return true;
}
function BusyOverlay() {
  return /* @__PURE__ */ jsx7(
    "div",
    {
      className: "absolute inset-0 z-20 flex items-center justify-center bg-surface-base/50 backdrop-blur-[1px]",
      role: "status",
      "aria-live": "polite",
      "aria-label": "\uB370\uC774\uD130 \uBD88\uB7EC\uC624\uB294 \uC911",
      children: /* @__PURE__ */ jsxs7("span", { className: "inline-flex items-center gap-2 rounded-sm border border-stroke-subtle bg-surface-raised px-3 py-1.5 text-[12px] font-medium text-text-secondary shadow-sm", children: [
        /* @__PURE__ */ jsx7(
          "span",
          {
            className: "size-3 animate-spin rounded-full border-2 border-accent border-r-transparent",
            "aria-hidden": true
          }
        ),
        "\uBD88\uB7EC\uC624\uB294 \uC911\u2026"
      ] })
    }
  );
}
export {
  DataTable
};
//# sourceMappingURL=index.js.map